import { Link } from "react-router-dom";
import {
  ArrowRight, Upload, FileCheck, Users, Shield, CheckCircle2,
  BookOpen, Globe, Clock, DollarSign, GraduationCap, Building
} from "lucide-react";

export default function PartnerProgramPage() {
  return (
    <div data-testid="partner-program-page">
      {/* Hero */}
      <section className="bg-[#1C2E4A] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-4">For Curriculum Providers &amp; Educational Programs</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              You teach. We handle the school records.
            </h1>
            <p className="mt-6 text-lg text-white/60 leading-relaxed max-w-2xl">
              Alabama Church School partners with curriculum providers and educational programs that want to focus on teaching — while ACS handles family enrollment, annual academic records review, and official academic documents.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <a
                href="https://partners.alabamachurchschool.com/become-a-partner"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="partner-cta-hero"
                className="bg-[#C8A97E] text-white hover:bg-[#B3956A] px-8 py-3.5 rounded-md transition-colors font-medium flex items-center justify-center gap-2 text-base"
              >
                Become a Partner <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="https://partners.alabamachurchschool.com"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/20 text-white hover:bg-white/10 px-8 py-3.5 rounded-md transition-colors font-medium flex items-center justify-center gap-2 text-base"
              >
                Visit Partner Portal
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Simple Process</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A]">How It Works</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                icon: Users,
                title: "Family Enrolls",
                desc: "A family enrolls directly with Alabama Church School through your partner-specific enrollment page on the ACS Partner Portal and pays Alabama Church School directly."
              },
              {
                step: "2",
                icon: Upload,
                title: "You Submit Records",
                desc: "As the educational partner, you upload the student's completed annual academic records package through your secure portal login."
              },
              {
                step: "3",
                icon: FileCheck,
                title: "ACS Reviews & Issues",
                desc: "ACS reviews the submitted records and issues the official Annual Academic Record & Report Card for each student."
              },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-[#1C2E4A] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-5">
                  {item.step}
                </div>
                <item.icon className="w-6 h-6 text-[#C8A97E] mx-auto mb-3" />
                <h3 className="font-serif text-lg font-semibold text-[#1C2E4A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#595959] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 md:py-28 bg-[#FAF9F6]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Partner Benefits</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A]">What Partners Get</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { icon: Shield, title: "Secure Portal", desc: "Your own login scoped to only your students — upload records, check review status, download issued documents." },
              { icon: Globe, title: "Your Own Enrollment Page", desc: "Families enroll at your custom partner URL (partners.alabamachurchschool.com/your-school) and are automatically linked to you." },
              { icon: FileCheck, title: "Official Academic Documents", desc: "ACS reviews records and issues the official Annual Academic Record & Report Card for every student." },
              { icon: Clock, title: "Review Status Tracking", desc: "See exactly where each student's records are — submitted, under review, correction needed, or completed." },
              { icon: GraduationCap, title: "Focus on Teaching", desc: "You handle curriculum and instruction. ACS handles enrollment paperwork, records review, and official documentation." },
              { icon: Building, title: "Church School Enrollment", desc: "Families are properly enrolled as church school students through Alabama Church School — no extra paperwork for you." },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#E5E2DC] p-6 hover:shadow-sm transition-shadow">
                <item.icon className="w-5 h-5 text-[#C8A97E] mb-3" />
                <h3 className="font-serif text-base font-semibold text-[#1C2E4A] mb-1.5">{item.title}</h3>
                <p className="text-xs text-[#595959] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Partner Pricing</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A]">Simple Annual Plans</h2>
            <p className="mt-3 text-sm text-[#595959] max-w-lg mx-auto">
              One annual registration covers your organization's use of the ACS records portal — record uploads, ACS review, and issued academic documents for your families.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Partner", price: "$249", period: "/year", students: "Up to 250 active students", popular: false },
              { name: "Partner Plus", price: "$499", period: "/year", students: "Up to 1,000 active students", popular: true },
              { name: "Partner Pro", price: "$999", period: "/year", students: "Up to 5,000 active students", popular: false },
            ].map((plan, i) => (
              <div
                key={i}
                className={`rounded-xl border p-8 text-center relative ${
                  plan.popular
                    ? "border-[#C8A97E] bg-[#1C2E4A] text-white shadow-xl scale-105"
                    : "border-[#E5E2DC] bg-[#FAF9F6]"
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C8A97E] text-white text-[10px] uppercase tracking-wider font-semibold px-4 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <h3 className={`font-serif text-lg font-semibold mb-2 ${plan.popular ? "text-white" : "text-[#1C2E4A]"}`}>
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className={`font-serif text-4xl font-bold ${plan.popular ? "text-[#C8A97E]" : "text-[#1C2E4A]"}`}>
                    {plan.price}
                  </span>
                  <span className={`text-sm ${plan.popular ? "text-white/50" : "text-[#595959]"}`}>{plan.period}</span>
                </div>
                <p className={`text-sm mb-6 ${plan.popular ? "text-white/60" : "text-[#595959]"}`}>
                  {plan.students}
                </p>
                <a
                  href="https://partners.alabamachurchschool.com/become-a-partner"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full py-2.5 rounded-md font-medium text-sm transition-colors ${
                    plan.popular
                      ? "bg-[#C8A97E] text-white hover:bg-[#B3956A]"
                      : "bg-[#1C2E4A] text-white hover:bg-[#152238]"
                  }`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-[#595959]/60 mt-8">
            More than 5,000 students? <a href="https://partners.alabamachurchschool.com/become-a-partner" target="_blank" rel="noopener noreferrer" className="text-[#C8A97E] hover:underline">Contact ACS for enterprise pricing</a>.
          </p>
        </div>
      </section>

      {/* Family Fees */}
      <section className="py-20 md:py-28 bg-[#FAF9F6]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Family Fees</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A]">What Families Pay</h2>
            <p className="mt-3 text-sm text-[#595959]">Family enrollment fees are separate from partner fees and paid directly by each family to Alabama Church School.</p>
          </div>

          <div className="space-y-4">
            {[
              { label: "Initial family enrollment", price: "$149.00", note: "One-time when the family first enrolls" },
              { label: "Annual family re-enrollment", price: "$99.00", note: "Each year after the first" },
              { label: "Annual records review (per student)", price: "$25.00", note: "Covers ACS review and official document" },
            ].map((fee, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#E5E2DC] p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#1C2E4A]">{fee.label}</p>
                  <p className="text-xs text-[#595959] mt-0.5">{fee.note}</p>
                </div>
                <p className="font-serif font-bold text-[#1C2E4A] text-xl shrink-0 ml-4">{fee.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-16 bg-white border-t border-[#E5E2DC]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="bg-[#1C2E4A]/5 rounded-xl p-6 space-y-3">
            <h3 className="font-serif text-lg font-semibold text-[#1C2E4A]">Important Notes</h3>
            <ul className="space-y-2">
              {[
                "Alabama Church School does not provide curriculum, lessons, grading, tutoring, or instruction through the partner portal.",
                "Each educational provider operates independently from Alabama Church School.",
                "Alabama Church School does not create, provide, evaluate, approve, or endorse any partner's curriculum or instructional services.",
                "Enrollment and academic documentation does not guarantee compliance with the homeschool, compulsory attendance, or education laws of the student's state of residence.",
                "The parent or legal guardian is responsible for determining and meeting all requirements that apply where the student resides.",
              ].map((note, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#595959]">
                  <CheckCircle2 className="w-4 h-4 text-[#C8A97E] shrink-0 mt-0.5" />
                  {note}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#1C2E4A]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white mb-4">Ready to Focus on Teaching?</h2>
          <p className="text-base text-white/60 mb-8 max-w-lg mx-auto">
            Let Alabama Church School handle enrollment and records so you can do what you do best — educate.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://partners.alabamachurchschool.com/become-a-partner"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="partner-cta-bottom"
              className="bg-[#C8A97E] text-white hover:bg-[#B3956A] px-8 py-3.5 rounded-md transition-colors font-medium flex items-center justify-center gap-2"
            >
              Become a Partner <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://partners.alabamachurchschool.com/partners"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-white/20 text-white hover:bg-white/10 px-8 py-3.5 rounded-md transition-colors font-medium flex items-center justify-center gap-2"
            >
              View Participating Partners
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
